/**
 * The textual outline: the diagram as a navigable list.
 *
 * This is not a fallback for the canvas, it is a peer to it. Every node is a
 * button that selects the node and reveals its source, so the whole diagram is
 * reachable with nothing but a keyboard and a screen reader — including the
 * branch labels, which are what a picture conveys through position alone.
 */
import { useMemo } from 'react';
import type { FlowGraph } from '../graph/model/types';
import { buildOutline } from '../diagram/outline';
import type { Translator } from '../i18n';
import type { MessageKey } from '../i18n';

export interface OutlinePanelProps {
  readonly graph: FlowGraph;
  readonly selectedNodeId: string | null;
  readonly onSelect: (nodeId: string) => void;
  readonly t: Translator['t'];
}

export function OutlinePanel({ graph, selectedNodeId, onSelect, t }: OutlinePanelProps) {
  const outline = useMemo(() => buildOutline(graph), [graph]);

  if (outline.entries.length === 0) {
    return <p className="p-6 text-center text-sm text-ink-faint">{t('a11y.outlineEmpty')}</p>;
  }

  return (
    <nav className="flex h-full min-h-0 flex-col bg-surface" aria-label={t('panel.outline')}>
      <p className="shrink-0 border-b border-border px-3 py-2 text-xs text-ink-muted">
        {t('diagram.nodeCount', { count: outline.nodeCount })} ·{' '}
        {t('diagram.edgeCount', { count: outline.edgeCount })}
      </p>

      <ol className="fs-scrollbar min-h-0 flex-1 overflow-y-auto py-1">
        {outline.entries.map((entry) => (
          <li key={entry.nodeId}>
            <button
              type="button"
              onClick={() => onSelect(entry.nodeId)}
              aria-current={entry.nodeId === selectedNodeId ? 'true' : undefined}
              className={[
                'flex w-full flex-col gap-0.5 px-3 py-1.5 text-left text-sm transition-colors',
                entry.nodeId === selectedNodeId
                  ? 'bg-accent-soft text-ink'
                  : 'text-ink hover:bg-surface-sunken',
              ].join(' ')}
              style={{ paddingLeft: `${12 + entry.depth * 14}px` }}
            >
              <span className="flex items-center gap-2">
                <span className="shrink-0 rounded bg-surface-sunken px-1.5 py-0.5 text-[10px] font-medium tracking-wide text-ink-muted uppercase">
                  {t(`a11y.role.${entry.role}` as MessageKey)}
                </span>
                <span className="min-w-0 truncate">{entry.label}</span>
              </span>

              {entry.viaLabel ? (
                <span className="text-xs text-ink-faint">
                  {t('a11y.outlineBranch', { label: entry.viaLabel })}
                </span>
              ) : null}

              {entry.outgoing.map((transition) => (
                <span
                  key={`${entry.nodeId}-${transition.targetId}-${transition.label}`}
                  className="text-xs text-ink-faint"
                >
                  {transition.isBackEdge
                    ? t('a11y.outlineLoopsBackTo', { label: transition.targetLabel })
                    : t('a11y.outlineLeadsTo', {
                        label: transition.label
                          ? `${transition.label} → ${transition.targetLabel}`
                          : transition.targetLabel,
                      })}
                </span>
              ))}
            </button>
          </li>
        ))}
      </ol>
    </nav>
  );
}
